import type { ReactNode } from "react";
import { FontSpecimen } from "@/components/assets";
import { cn } from "@/lib/utils";
import { FONT_LABELS, FONT_STACKS, OWN_FONT } from "./template-schema";
import "./bundled-fonts.css";

// "Ag": a capital and the most telling lowercase glyph (bowl and descender).
const SPECIMEN = "Ag";

interface FontPickerProps {
    value: string;
    onChange: (value: string) => void;
    /** The uploaded font's file URL, for the Your font card's specimen. */
    ownFontUrl: string | null;
}

/**
 * One card per font, each drawn in its own face — for a typeface, seeing it is
 * the choice. Ported from vouchers' design-template font picker so the two
 * products' editors read alike; the fourth card is the workspace's own font,
 * whose file is chosen in the field below the cards.
 */
export function FontPicker({ value, onChange, ownFontUrl }: FontPickerProps) {
    const card = (key: string, label: string, face: ReactNode) => {
        const selected = key === value;
        return (
            <button
                key={key}
                type="button"
                aria-pressed={selected}
                aria-label={label}
                onClick={() => onChange(key)}
                className={cn(
                    "flex flex-col items-center gap-1.5 rounded-md border px-2 py-3 transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    selected
                        ? "border-primary bg-primary/5"
                        : "border-border bg-background hover:border-input hover:bg-muted/40",
                )}
            >
                {face}
                <span className={cn("text-xs", selected ? "font-medium text-primary" : "text-muted-foreground")}>
                    {label}
                </span>
            </button>
        );
    };

    return (
        <fieldset className="m-0 grid min-w-0 grid-cols-4 gap-2 border-0 p-0">
            <legend className="sr-only">Font</legend>
            {Object.entries(FONT_LABELS).map(([key, label]) =>
                card(
                    key,
                    label,
                    <span
                        aria-hidden="true"
                        className="text-2xl leading-none text-foreground"
                        style={{ fontFamily: FONT_STACKS[key] }}
                    >
                        {SPECIMEN}
                    </span>,
                ),
            )}
            {card(
                OWN_FONT,
                "Your font",
                ownFontUrl ? (
                    <FontSpecimen
                        url={ownFontUrl}
                        label="Your font"
                        className="text-2xl leading-none text-foreground"
                    />
                ) : (
                    <span aria-hidden="true" className="text-2xl leading-none text-muted-foreground">
                        +
                    </span>
                ),
            )}
        </fieldset>
    );
}
